import FlexiText from "slotworks/graphics/elements/flexi-text";
import Layers from "slotworks/graphics/layers/layers";
import { Services } from "slotworks/services/services";
import { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import HeadlessSmartUIGroup from "slotworks/smart-ui/smart-ui-group";
import SmartUIManager from "slotworks/smart-ui/smart-ui-manager";
import * as TweenJS from "slotworks/utils/tween";
import BetBarValueText from "./bet-bar-value-text";
import AgsBetBarValueText from "ags/components/ags-bet-bar-value-text";

export default class BetBarComponent {

    protected static FREESPINS_TOTAL_VALUE: string = "freespins_total_value";
    protected static FREESPINS_REMAINING_VALUE: string = "freespins_remaining_value";
    protected static FREESPINS_X_OF_Y_VALUE: string = "freespins_x_of_y_value";

    public balanceTickTime: number = 0;
    public balanceUpdateDelay: number = 0;

    protected currentType: BetBarType;

    protected freespinsTotalText: FlexiText;
    protected freespinsRemainingText: FlexiText;
    protected freespinsXOfyText: FlexiText;

    protected  balanceTween: TweenJS.Tween;

    protected  balanceValue: BetBarValueText;
    protected  totalWinValue: BetBarValueText;
    protected  currentWinValue: BetBarValueText;

    protected  balance: number = 0;
    protected  displayBalance: number = 0;
    protected  totalWin: number = 0;

    protected  totalFreeSpins: number = 0;
    protected  remainingFreespins: number = 0;
    protected  currentWin: number = 0;

    protected  SmartUIGroups: HeadlessSmartUIGroup[] = [];

    protected  layer: Layers;

    public constructor() {
        this.layer = Layers.BetBar;

        this.balanceValue = new AgsBetBarValueText(this.layer, "balance");
        this.totalWinValue = new AgsBetBarValueText(this.layer, "total_win", true);
        this.currentWinValue = new AgsBetBarValueText(this.layer, "current_win", true);

        let group = SmartUIManager.newSmartUIGroup(this.layer, BetBarType.DEFAULT.toString(), "betbar_" + BetBarType.DEFAULT.toString());
        group.addEnableCondition([SmartUIFlag.IDLE]);
        group.addDisableCondition([SmartUIFlag.PROMPT]);
        group.addDisableCondition([SmartUIFlag.AUTO_SPIN]);
        group.addDisableCondition([SmartUIFlag.AUTO_SPIN_SELECT]);
        group.addDisableCondition([SmartUIFlag.GAMBLE_UI]);
        group.addDisableCondition([SmartUIFlag.EXTERNAL_DISABLE]);

        this.SmartUIGroups.push(group);

        if (this.layer.hasScene(BetBarType.FREESPINS.toString())) {
            group = SmartUIManager.newSmartUIGroup(this.layer, BetBarType.FREESPINS.toString(), "betbar_" + BetBarType.FREESPINS.toString());
            group.addDisableCondition([SmartUIFlag.FREE_SPINS]);
            group.addDisableCondition([SmartUIFlag.AUTO_SPIN]);
            group.addDisableCondition([SmartUIFlag.AUTO_SPIN_SELECT]);
            group.addDisableCondition([SmartUIFlag.GAMBLE_UI]);
            group.addDisableCondition([SmartUIFlag.EXTERNAL_DISABLE]);

            this.SmartUIGroups.push(group);
        }

        SmartUIManager.onOptionsChanged.add(() => this.updateTexts());

        this.setType(BetBarType.DEFAULT);

        Services.transaction.currentWinUpdate.add((value: number) => this.setCurrentWin(value));
        Services.transaction.totalWinUpdate.add((value: number) => this.setTotalWin(value));
        Services.transaction.balanceUpdate.add((value: number) => this.setBalance(value));
    }

    public setType(type: BetBarType) {
        if (this.currentType === type) {
            return;
        }

        this.currentType = type;

        SmartUIManager.show("betbar_" + type.toString()).then(() => {
            this.refresh();
        });
    }

    // Free spins
    public setTotalFreespins(value: number) {
        this.totalFreeSpins = value;
        this.updateTexts();
    }

    public setFreespinsPlayed(value: number) {
        this.remainingFreespins = this.totalFreeSpins - value;
        this.updateTexts();
    }

    protected setCurrentWin(value: number) {
        this.currentWin = value;
        this.updateTexts();
    }

    protected setBalance(value: number) {
        if (this.balanceTween) {
            this.balanceTween.stop();
            TweenJS.remove(this.balanceTween);
            this.displayBalance = this.balance;
        }

        this.balance = value;

        if (this.balanceTickTime > 0 && this.balance > this.displayBalance) {
            this.balanceTween = new TweenJS.Tween(this)
                .to({ displayBalance: value }, this.balanceTickTime)
                .delay(this.balanceUpdateDelay)
                .onUpdate(() => this.updateTexts())
                .start();
        } else {
            this.displayBalance = this.balance;
        }

        this.updateTexts();
    }

    protected setTotalWin(value: number) {
        this.totalWin = value;
        this.updateTexts();
    }

    protected refresh() {
        this.setupTexts();
        Services.transaction.setWinnings(this.currentWin, this.totalWin).execute();
        this.setBalance(this.balance);
    }

    protected setupTexts() {
        this.balanceValue.refresh();
        this.totalWinValue.refresh();
        this.currentWinValue.refresh();

        this.freespinsTotalText = this.layer.getFlexiText(BetBarComponent.FREESPINS_TOTAL_VALUE);
        this.freespinsRemainingText = this.layer.getFlexiText(BetBarComponent.FREESPINS_REMAINING_VALUE);
        this.freespinsXOfyText = this.layer.getFlexiText(BetBarComponent.FREESPINS_X_OF_Y_VALUE);
    }

    protected updateTexts() {
        this.balanceValue.update(this.displayBalance);
        this.totalWinValue.update(this.totalWin);
        this.currentWinValue.update(this.currentWin);

        if (this.freespinsTotalText) {
            if (this.totalFreeSpins > 0) {
                this.freespinsTotalText.setText(this.totalFreeSpins.toString());
            } else {
                this.freespinsTotalText.setText("");
            }
        }
        if (this.freespinsRemainingText) {
            this.freespinsRemainingText.setText(this.remainingFreespins.toString());
        }
        if (this.freespinsXOfyText) {
            if (this.totalFreeSpins > 0) {
                const tokens = {
                    current_spin: this.totalFreeSpins - this.remainingFreespins,
                    total_spins: this.totalFreeSpins
                };

                this.freespinsXOfyText.setText(Services.translations.get("bar.xoy", tokens));
            } else {
                this.freespinsXOfyText.setText("");
            }
        }
    }
}

export class BetBarType {

    public static DEFAULT: BetBarType = new BetBarType("default");
    public static FREESPINS: BetBarType = new BetBarType("freespins");

    constructor(private type: string) {
    }

    public toString(): string {
        return this.type;
    }
}
